/**
 * Carding Settings Master — per-(machine, count) LMW CARD LC 363 settings.
 *
 * Route: /production/carding-settings
 *
 * Drives the alert engine. Standards seeded here are auto-fetched into a
 * Carding production log's `display_data` when the supervisor starts a
 * new log, and the active standard's tolerances drive the alert rules
 * that fire on APPROVED.
 */
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button, Card, Col, DatePicker, Divider, Drawer, Form, Input, InputNumber,
  Modal, Row, Select, Space, Switch, Table, Tabs, Tag, Tooltip, Typography, message,
} from 'antd'
import {
  CalculatorOutlined, DownloadOutlined, EditOutlined,
  PlusOutlined, ReloadOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'

import {
  cardingApi, type CardStandard, type CalcResult,
} from '@/features/production/cardingApi'
import {
  checkpointsApi, type CardingWasteCategory,
} from '@/features/production/checkpointsApi'
import { usePlantId } from '@/shared/hooks/usePlantId'
import { usePermission } from '@/shared/hooks/usePermission'
import apiClient from '@/shared/api/client'

const { Title, Text, Paragraph } = Typography

interface MachineLite { id: string; machine_code: string; machine_name: string }
interface CountLite { id: string; count_code: string; count_ne: number; description?: string }

export default function CardingSettingsPage() {
  const qc = useQueryClient()
  const { plantId } = usePlantId()
  const [createOpen, setCreateOpen] = useState(false)
  const [calcOpen, setCalcOpen] = useState(false)
  const [machineFilter, setMachineFilter] = useState<string | undefined>()
  const [activeOnly, setActiveOnly] = useState(true)
  // Sprint 0144 follow-up — Edit state.
  // editingRow !== null → the drawer opens in Edit mode pre-filled with
  // the row's values. supersedeMode toggles the save strategy:
  //   off → PATCH the row in place (typo fix path)
  //   on  → deactivate the row + create new with today as effective_from
  //          (preserves history for genuine standards revisions)
  const [editingRow, setEditingRow] = useState<CardStandard | null>(null)
  const [supersedeMode, setSupersedeMode] = useState(false)
  const [form] = Form.useForm()
  const [editForm] = Form.useForm()

  const listQ = useQuery({
    enabled: !!plantId,
    queryKey: ['carding-settings', plantId, machineFilter, activeOnly],
    queryFn: () => cardingApi.listSettings({
      plant_id: plantId!,
      machine_id: machineFilter,
      active_only: activeOnly,
    }),
  })

  const machinesQ = useQuery({
    enabled: !!plantId,
    queryKey: ['machines-carding', plantId],
    queryFn: async () => {
      const r = await apiClient.get('/masters/machines', {
        params: { plant_id: plantId, is_active: true, size: 500 },
      })
      // The masters endpoint returns a PaginatedResponse {items: []} —
      // unwrap defensively in case other variants return a raw array.
      const raw: any[] = Array.isArray(r.data) ? r.data : (r.data?.items ?? [])
      return raw
        .filter((m) => (m.machine_type ?? m.section ?? '').toUpperCase() === 'CARDING')
        .map((m): MachineLite => ({
          id: m.id,
          machine_code: m.machine_code ?? m.machine_id_str ?? '',
          machine_name: m.machine_name ?? m.name ?? '',
        }))
    },
  })

  const countsQ = useQuery({
    queryKey: ['counts-for-carding'],
    queryFn: async () => {
      const r = await apiClient.get('/masters/counts', {
        params: { size: 500 },
      })
      const raw: any[] = Array.isArray(r.data) ? r.data : (r.data?.items ?? [])
      return raw.map((c): CountLite => ({
        id: c.id,
        count_code: c.count_code ?? '',
        count_ne: c.count_ne ?? 0,
        description: c.description,
      }))
    },
  })

  const machineOptions = (machinesQ.data ?? []).map((m) => ({
    label: `${m.machine_code} — ${m.machine_name}`, value: m.id,
  }))
  const countOptions = (countsQ.data ?? []).map((c) => ({
    label: `${c.count_code} (${c.count_ne}Ne)`, value: c.id,
  }))

  const createMut = useMutation({
    mutationFn: (body: Parameters<typeof cardingApi.createSetting>[0]) =>
      cardingApi.createSetting(body),
    onSuccess: () => {
      message.success('Setting created')
      setCreateOpen(false)
      form.resetFields()
      qc.invalidateQueries({ queryKey: ['carding-settings'] })
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Failed to create'),
  })

  const deactivateMut = useMutation({
    mutationFn: (id: string) =>
      cardingApi.updateSetting(id, { is_active: false, effective_to: dayjs().format('YYYY-MM-DD') }),
    onSuccess: () => {
      message.success('Setting deactivated')
      qc.invalidateQueries({ queryKey: ['carding-settings'] })
    },
  })

  // Sprint 0144 follow-up — PATCH for the "Edit in place" path.
  const updateMut = useMutation({
    mutationFn: (args: { id: string; body: Partial<CardStandard> }) =>
      cardingApi.updateSetting(args.id, args.body),
    onSuccess: () => {
      message.success('Setting updated')
      setEditingRow(null)
      editForm.resetFields()
      qc.invalidateQueries({ queryKey: ['carding-settings'] })
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Failed to update'),
  })

  // Excel download — uses the cardingApi helper which fires a browser save.
  const [exporting, setExporting] = useState(false)
  const exportHandler = async () => {
    if (!plantId) { message.error('Plant not loaded'); return }
    setExporting(true)
    try {
      await cardingApi.exportSettings({
        plant_id: plantId,
        machine_id: machineFilter,
        active_only: activeOnly,
      })
      message.success('Downloaded carding_settings.xlsx')
    } catch (e) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      message.error((e as any)?.response?.data?.detail ?? 'Export failed')
    } finally {
      setExporting(false)
    }
  }

  // Sprint carding-waste-categories — existing page content stays the
  // default/active tab so current users see no change; the new manager
  // lives in a second tab.
  const settingsTabContent = (
    <>
      <Row justify="space-between" align="middle">
        <Col>
          <Title level={3} style={{ marginBottom: 0 }}>Carding Settings Master</Title>
          <Text type="secondary">
            LMW CARD LC 363 settings per machine × count. Drives auto-fetch +
            alert engine. Standards stay active until an effective_to date or
            explicit deactivation.
          </Text>
        </Col>
        <Col>
          <Space>
            <Button
              icon={<CalculatorOutlined />}
              onClick={() => setCalcOpen(true)}
            >
              Carding Calculator
            </Button>
            <Button
              icon={<DownloadOutlined />}
              loading={exporting}
              onClick={exportHandler}
            >
              Export Excel
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreateOpen(true)}
            >
              New Setting
            </Button>
          </Space>
        </Col>
      </Row>

      <Card size="small" style={{ marginTop: 16, marginBottom: 16 }}>
        <Space wrap>
          <Text>Machine:</Text>
          <Select
            allowClear
            showSearch
            style={{ width: 280 }}
            value={machineFilter}
            onChange={setMachineFilter}
            placeholder="All carding machines"
            options={machineOptions}
            filterOption={(input, option) =>
              (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
          <Tooltip title="When off, deactivated/superseded settings appear too — that's your audit trail. Each row's From/To dates show when it was in effect.">
            <Space size={4}>
              <Switch
                checked={!activeOnly}
                onChange={(v) => setActiveOnly(!v)}
                size="small"
              />
              <Text>Show history</Text>
            </Space>
          </Tooltip>
          <Button icon={<ReloadOutlined />} onClick={() => listQ.refetch()}>
            Refresh
          </Button>
        </Space>
      </Card>

      <Table<CardStandard>
        size="small"
        rowKey="id"
        loading={listQ.isLoading}
        dataSource={listQ.data ?? []}
        pagination={{ pageSize: 50 }}
        scroll={{ x: 'max-content' }}
        columns={[
          {
            title: 'Machine',
            dataIndex: 'machine_id',
            key: 'machine_id',
            width: 140,
            render: (id: string | null) => {
              const m = machinesQ.data?.find((x) => x.id === id)
              return m ? m.machine_code : '(any)'
            },
          },
          {
            title: 'Count',
            dataIndex: 'count_id',
            key: 'count_id',
            width: 100,
            render: (id: string | null) => {
              const c = countsQ.data?.find((x) => x.id === id)
              return c ? c.count_code : '(any)'
            },
          },
          { title: 'Hank', dataIndex: 'card_sliver_hank', key: 'hank',
            align: 'right', width: 80 },
          { title: 'Delivery m/min', dataIndex: 'delivery_speed_m_min', key: 'speed',
            align: 'right', width: 110 },
          { title: 'Eff %', dataIndex: 'standard_efficiency_pct', key: 'eff',
            align: 'right', width: 70 },
          { title: 'Can kg', dataIndex: 'standard_can_kg', key: 'can',
            align: 'right', width: 70 },
          { title: 'Waste %', dataIndex: 'standard_waste_pct', key: 'waste',
            align: 'right', width: 70 },
          // Sprint 0140 — Last QC measurement.
          // Shows "DD MMM (Nd ago)" — flags red when >60 days old.
          {
            title: 'Last QC',
            dataIndex: 'qc_measured_at',
            key: 'qc_measured_at',
            width: 120,
            render: (v: string | null, r: any) => {
              if (!v) return <Tag>Never</Tag>
              const days = dayjs().diff(dayjs(v), 'day')
              const stale = days > 60
              return (
                <Tooltip title={`Sample size: ${r.qc_sample_kg ?? '?'} kg`}>
                  <Tag color={stale ? 'red' : days > 30 ? 'orange' : 'green'}>
                    {dayjs(v).format('DD MMM')} ({days}d ago)
                  </Tag>
                </Tooltip>
              )
            },
          },
          { title: 'Can length m', dataIndex: 'can_length_m', key: 'can_length',
            align: 'right', width: 110,
            render: (v: number | null) => v ?? '-' },
          { title: 'Hank tol %', dataIndex: 'hank_tolerance_pct', key: 'tol',
            align: 'right', width: 90,
            render: (v: number | null) => v ?? '-' },
          { title: 'From', dataIndex: 'effective_from', key: 'from', width: 100,
            render: (v: string) => dayjs(v).format('DD MMM YY') },
          { title: 'To', dataIndex: 'effective_to', key: 'to', width: 100,
            render: (v: string | null) => v ? dayjs(v).format('DD MMM YY') : '-' },
          {
            title: 'Status',
            dataIndex: 'is_active',
            key: 'is_active',
            width: 90,
            render: (active: boolean) => active ? (
              <Tag color="green">Active</Tag>
            ) : (
              <Tag>Inactive</Tag>
            ),
          },
          // Sprint 0144 follow-up — audit trail. Shows the last edit
          // timestamp; tooltip carries the editor's user-id for now
          // (the user master can resolve it server-side later).
          {
            title: 'Updated',
            dataIndex: 'updated_at',
            key: 'updated_at',
            width: 130,
            render: (v: string | null, r: any) => {
              if (!v) return <Text type="secondary">—</Text>
              return (
                <Tooltip title={r.updated_by ? `by ${r.updated_by.slice(0, 8)}…` : 'system'}>
                  <Text style={{ fontSize: 12 }}>
                    {dayjs(v).format('DD MMM YY HH:mm')}
                  </Text>
                </Tooltip>
              )
            },
          },
          {
            title: '',
            key: 'action',
            fixed: 'right',
            width: 160,
            render: (_, r) => r.is_active ? (
              <Space size={4}>
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => {
                    setEditingRow(r)
                    setSupersedeMode(false)
                    // Pre-fill the edit form from the row — dates need
                    // wrapping in dayjs() because AntD DatePicker only
                    // understands its internal type.
                    editForm.setFieldsValue({
                      ...r,
                      effective_from: r.effective_from ? dayjs(r.effective_from) : null,
                      effective_to: r.effective_to ? dayjs(r.effective_to) : null,
                      qc_measured_at: r.qc_measured_at ? dayjs(r.qc_measured_at) : null,
                    })
                  }}
                >
                  Edit
                </Button>
                <Button
                  size="small"
                  danger
                  onClick={() => Modal.confirm({
                    title: 'Deactivate this setting?',
                    content: 'Future production logs will fall back to plant-wide defaults until a new active setting exists.',
                    onOk: () => deactivateMut.mutate(r.id),
                  })}
                >
                  Deactivate
                </Button>
              </Space>
            ) : null,
          },
        ]}
      />

      {/* New Setting Drawer */}
      <Drawer
        title="New Carding Setting"
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        width={640}
        destroyOnClose
      >
        <Paragraph type="secondary">
          One setting row per (machine, count) combo. Leave machine_id or
          count_id blank to apply broadly. Standard fields are required;
          LMW display fields + tolerances are optional but drive alerts.
        </Paragraph>
        <Form
          form={form}
          layout="vertical"
          onFinish={(v) => {
            if (!plantId) {
              message.error('Plant not loaded')
              return
            }
            createMut.mutate({
              plant_id: plantId,
              machine_id: v.machine_id,
              count_id: v.count_id,
              yarn_type: v.yarn_type,
              card_sliver_hank: v.card_sliver_hank,
              delivery_speed_m_min: v.delivery_speed_m_min,
              standard_efficiency_pct: v.standard_efficiency_pct ?? 90,
              standard_can_kg: v.standard_can_kg ?? 25,
              standard_waste_pct: v.standard_waste_pct ?? 4,
              // Sprint 0140 — QC metadata
              qc_measured_at: v.qc_measured_at
                ? (v.qc_measured_at as dayjs.Dayjs).format('YYYY-MM-DD')
                : undefined,
              qc_sample_kg: v.qc_sample_kg,
              cylinder_speed_rpm: v.cylinder_speed_rpm,
              flats_speed_mm_min: v.flats_speed_mm_min,
              lickerin_1_speed_rpm: v.lickerin_1_speed_rpm,
              lickerin_2_speed_rpm: v.lickerin_2_speed_rpm,
              lickerin_3_speed_rpm: v.lickerin_3_speed_rpm,
              feed_roller_rpm: v.feed_roller_rpm,
              card_draft: v.card_draft,
              chute_feed_inverter_hz: v.chute_feed_inverter_hz,
              feed_thickness_mm: v.feed_thickness_mm,
              chute_pressure_pa: v.chute_pressure_pa,
              can_length_m: v.can_length_m,
              hank_tolerance_pct: v.hank_tolerance_pct,
              correction_allowed_pct: v.correction_allowed_pct,
              idle_minutes_threshold: v.idle_minutes_threshold,
              sliver_break_threshold: v.sliver_break_threshold,
              chute_fault_threshold: v.chute_fault_threshold,
              double_lap_threshold: v.double_lap_threshold,
              effective_from: (v.effective_from as dayjs.Dayjs).format('YYYY-MM-DD'),
              effective_to: v.effective_to
                ? (v.effective_to as dayjs.Dayjs).format('YYYY-MM-DD') : undefined,
              notes: v.notes,
            } as never)
          }}
          initialValues={{
            effective_from: dayjs(),
            standard_efficiency_pct: 90,
            standard_can_kg: 25,
            standard_waste_pct: 4,
            hank_tolerance_pct: 3,
          }}
        >
          <Divider orientation="left">Scope</Divider>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="machine_id" label="Machine">
                <Select allowClear showSearch options={machineOptions}
                  filterOption={(input, option) =>
                    (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  placeholder="(any)" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="count_id" label="Count">
                <Select allowClear options={countOptions} placeholder="(any)" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Core production (required)</Divider>
          <Row gutter={12}>
            <Col span={6}>
              <Form.Item name="card_sliver_hank" label="Sliver hank"
                rules={[{ required: true }]}>
                <InputNumber min={0} step={0.001} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="delivery_speed_m_min" label="Delivery speed m/min"
                rules={[{ required: true }]}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="standard_efficiency_pct" label="Std efficiency %">
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="standard_can_kg" label="Std can kg">
                <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={6}>
              <Form.Item
                name="standard_waste_pct"
                label="Std waste %"
                tooltip="Industry norm 3-5% (flat strips + licker-in droppings + sliver clearer). Auto-suggests Carding waste in the Daily Shift Log so BR mass balance closes correctly. Tune per machine — older Trützschler cards waste more (~5%); newer LMW C5/1 cards waste less (~3%)."
              >
                <InputNumber min={0} max={100} step={0.1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="can_length_m" label="Can length m">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          {/* Sprint 0140 — QC measurement metadata.
              Whenever QC weighs a sample through this card and updates
              the std waste %, they also record the date + sample size.
              This drives the "Last QC" badge in the table and a "QC stale"
              warning when measurement is > 60 days old. */}
          <Divider orientation="left">QC measurement (per spec)</Divider>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item
                name="qc_measured_at"
                label="Last QC measured on"
                tooltip="Date QC last weighed a sample through this card. Mills typically re-measure monthly. Stale measurements (>60 days) get a warning."
              >
                <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="qc_sample_kg"
                label="QC sample size kg"
                tooltip="Size of the cotton sample QC fed through the card during the measurement. Larger sample = more reliable %. Industry norm 50-100 kg."
              >
                <InputNumber min={0} step={1} style={{ width: '100%' }} placeholder="e.g. 50" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">LMW display settings (optional)</Divider>
          <Row gutter={12}>
            <Col span={6}><Form.Item name="cylinder_speed_rpm" label="Cylinder rpm"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={6}><Form.Item name="flats_speed_mm_min" label="Flats mm/min"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={6}><Form.Item name="lickerin_1_speed_rpm" label="Lickerin 1 rpm"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={6}><Form.Item name="lickerin_2_speed_rpm" label="Lickerin 2 rpm"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={6}><Form.Item name="lickerin_3_speed_rpm" label="Lickerin 3 rpm"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={6}><Form.Item name="feed_roller_rpm" label="Feed roller rpm"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={6}><Form.Item name="card_draft" label="Card draft"><InputNumber min={0} step={0.01} style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={6}><Form.Item name="chute_feed_inverter_hz" label="Chute inv Hz"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={6}><Form.Item name="feed_thickness_mm" label="Feed thickness mm"><InputNumber min={0} step={0.01} style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={6}><Form.Item name="chute_pressure_pa" label="Chute pressure Pa"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
          </Row>

          <Divider orientation="left">Alert tolerances</Divider>
          <Row gutter={12}>
            <Col span={6}><Form.Item name="hank_tolerance_pct" label="Hank tolerance %"><InputNumber min={0} step={0.1} style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={6}><Form.Item name="correction_allowed_pct" label="Correction allowed %"><InputNumber min={0} step={0.1} style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={6}><Form.Item name="idle_minutes_threshold" label="Idle min threshold"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={6}><Form.Item name="sliver_break_threshold" label="Sliver break count"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={6}><Form.Item name="chute_fault_threshold" label="Chute fault count"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={6}><Form.Item name="double_lap_threshold" label="Double lap count"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
          </Row>

          <Divider orientation="left">Validity</Divider>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="effective_from" label="Effective from"
                rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="effective_to" label="Effective to (optional)">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={createMut.isPending}>
                Create
              </Button>
              <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Drawer>

      {/* Edit Setting Drawer — Sprint 0144 follow-up.
          Two save strategies via the "Supersede" toggle:
            • OFF (default): PATCH the row in place — fast, good for typos
            • ON: deactivate old (effective_to=today) + create new with
              today as effective_from — preserves the timeline so reports
              can answer "what was the standard on date X?"
      */}
      <Drawer
        title={
          <Space>
            <span>Edit Carding Setting</span>
            {editingRow && (
              <Tag color="blue" style={{ marginLeft: 8 }}>
                {(machinesQ.data?.find((m) => m.id === editingRow.machine_id)?.machine_code) || '(any)'}
                {' · '}
                {(countsQ.data?.find((c) => c.id === editingRow.count_id)?.count_code) || '(any)'}
              </Tag>
            )}
          </Space>
        }
        open={!!editingRow}
        onClose={() => { setEditingRow(null); editForm.resetFields() }}
        width={640}
        destroyOnClose
      >
        <Paragraph type="secondary" style={{ marginBottom: 4 }}>
          Edit any field. The save mode below decides whether the change
          overwrites this row or creates a dated supersession.
        </Paragraph>
        <Card
          size="small"
          style={{ marginBottom: 12, background: '#fafafa' }}
        >
          <Space size={16} wrap>
            <Space size={4}>
              <Switch
                size="small"
                checked={supersedeMode}
                onChange={setSupersedeMode}
              />
              <Text strong>Supersede with new dated setting</Text>
            </Space>
            <Tooltip title="When ON: this row gets deactivated (effective_to = today) and a new row is created starting today with your edited values. Past reports stay accurate. When OFF: this row is patched in place — use for typo fixes only.">
              <Text type="secondary" style={{ fontSize: 12 }}>
                {supersedeMode
                  ? 'Deactivate old + create new (preserves history)'
                  : 'Patch in place (fast, no history trace)'}
              </Text>
            </Tooltip>
          </Space>
        </Card>
        {editingRow && (
          <Form
            form={editForm}
            layout="vertical"
            onFinish={(v) => {
              if (!plantId) { message.error('Plant not loaded'); return }
              // Strip Form-only objects and re-format dates to ISO strings.
              const body: Record<string, unknown> = { ...v }
              if (v.effective_from) {
                body.effective_from = (v.effective_from as dayjs.Dayjs).format('YYYY-MM-DD')
              }
              if (v.effective_to) {
                body.effective_to = (v.effective_to as dayjs.Dayjs).format('YYYY-MM-DD')
              }
              if (v.qc_measured_at) {
                body.qc_measured_at = (v.qc_measured_at as dayjs.Dayjs).format('YYYY-MM-DD')
              }
              if (supersedeMode) {
                // Deactivate the existing row with today as effective_to,
                // then create a fresh one with today as effective_from.
                const today = dayjs().format('YYYY-MM-DD')
                cardingApi.updateSetting(editingRow.id, {
                  is_active: false,
                  effective_to: today,
                }).then(() => {
                  return cardingApi.createSetting({
                    ...(body as Parameters<typeof cardingApi.createSetting>[0]),
                    plant_id: plantId,
                    effective_from: today,
                  })
                }).then(() => {
                  message.success('Setting superseded — new dated row created')
                  setEditingRow(null)
                  editForm.resetFields()
                  qc.invalidateQueries({ queryKey: ['carding-settings'] })
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                }).catch((e: any) => {
                  message.error(e?.response?.data?.detail ?? 'Supersession failed')
                })
              } else {
                updateMut.mutate({ id: editingRow.id, body: body as Partial<CardStandard> })
              }
            }}
          >
            <Divider orientation="left">Core production</Divider>
            <Row gutter={12}>
              <Col span={6}>
                <Form.Item name="card_sliver_hank" label="Sliver hank">
                  <InputNumber min={0} step={0.001} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="delivery_speed_m_min" label="Delivery m/min">
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="standard_efficiency_pct" label="Std eff %">
                  <InputNumber min={0} max={100} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="standard_can_kg" label="Std can kg">
                  <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col span={6}>
                <Form.Item name="standard_waste_pct" label="Std waste %">
                  <InputNumber min={0} max={100} step={0.1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="can_length_m" label="Can length m">
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="hank_tolerance_pct" label="Hank tol %">
                  <InputNumber min={0} max={100} step={0.1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left">QC</Divider>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="qc_measured_at" label="Last QC measured at">
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="qc_sample_kg" label="QC sample kg">
                  <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left">Effective window</Divider>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="effective_from" label="Effective from">
                  <DatePicker style={{ width: '100%' }} disabled={supersedeMode}/>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="effective_to" label="Effective to">
                  <DatePicker style={{ width: '100%' }} disabled={supersedeMode}/>
                </Form.Item>
              </Col>
            </Row>
            {supersedeMode && (
              <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: -8, marginBottom: 12 }}>
                In Supersede mode, today's date is used automatically — old row
                gets effective_to=today, new row gets effective_from=today.
              </Text>
            )}

            <Form.Item name="notes" label="Notes">
              <Input.TextArea rows={2} />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={updateMut.isPending}>
                  {supersedeMode ? 'Save as new dated setting' : 'Save changes'}
                </Button>
                <Button onClick={() => { setEditingRow(null); editForm.resetFields() }}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Drawer>

      {/* Calculator Modal */}
      <CalcModal open={calcOpen} onClose={() => setCalcOpen(false)} />
    </>
  )

  return (
    <div style={{ padding: 24 }}>
      <Tabs
        defaultActiveKey="settings"
        items={[
          {
            key: 'settings',
            label: 'Card Settings',
            children: settingsTabContent,
          },
          {
            key: 'waste-categories',
            label: 'Waste Categories',
            children: <WasteCategoriesTab />,
          },
        ]}
      />
    </div>
  )
}

// ─── Calculator modal ─────────────────────────────────────────────

function CalcModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [form] = Form.useForm()
  const [result, setResult] = useState<CalcResult | null>(null)
  const calcMut = useMutation({
    mutationFn: cardingApi.calc,
    onSuccess: (r) => setResult(r),
  })

  return (
    <Modal
      title="Carding Production Calculator"
      open={open}
      onCancel={() => { onClose(); setResult(null); form.resetFields() }}
      footer={null}
      width={560}
      destroyOnClose
    >
      <Paragraph type="secondary">
        Section 17 + 18 formulas. Sliver Ktex = 0.5905 ÷ hank. Theoretical kg
        = delivery × 60 × hours × ktex ÷ 1000. Missing inputs return null,
        not 0 — confidence reflects which inputs were supplied.
      </Paragraph>
      <Form
        form={form}
        layout="vertical"
        onFinish={(v) => calcMut.mutate(v)}
      >
        <Row gutter={12}>
          <Col span={12}><Form.Item name="sliver_hank" label="Sliver hank"><InputNumber min={0} step={0.001} style={{ width: '100%' }} /></Form.Item></Col>
          <Col span={12}><Form.Item name="can_length_m" label="Can length m"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}><Form.Item name="delivery_speed_mpm" label="Delivery speed m/min"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
          <Col span={12}><Form.Item name="runtime_hours" label="Runtime hours"><InputNumber min={0} step={0.1} style={{ width: '100%' }} /></Form.Item></Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}><Form.Item name="actual_production_kg" label="Actual production kg"><InputNumber min={0} step={0.1} style={{ width: '100%' }} /></Form.Item></Col>
          <Col span={12}><Form.Item name="standard_efficiency_pct" label="Std efficiency %"><InputNumber min={0} max={100} style={{ width: '100%' }} /></Form.Item></Col>
        </Row>
        <Space>
          <Button type="primary" htmlType="submit" loading={calcMut.isPending}
            icon={<CalculatorOutlined />}>
            Calculate
          </Button>
          <Button onClick={() => { form.resetFields(); setResult(null) }}>
            Reset
          </Button>
        </Space>
      </Form>
      {result && (
        <Card size="small" style={{ marginTop: 16 }}>
          <Row gutter={[12, 12]}>
            <Col span={12}><Text type="secondary">Sliver Ktex:</Text> <Text strong>{result.sliver_ktex ?? '—'}</Text></Col>
            <Col span={12}><Text type="secondary">Can content kg:</Text> <Text strong>{result.can_content_kg ?? '—'}</Text></Col>
            <Col span={12}><Text type="secondary">Theoretical kg:</Text> <Text strong>{result.theoretical_production_kg ?? '—'}</Text></Col>
            <Col span={12}><Text type="secondary">Expected kg:</Text> <Text strong>{result.expected_production_kg ?? '—'}</Text></Col>
            <Col span={12}><Text type="secondary">Efficiency %:</Text> <Text strong>{result.actual_efficiency_pct ?? '—'}</Text></Col>
            <Col span={12}><Text type="secondary">Cans produced:</Text> <Text strong>{result.cans_produced ?? '—'}</Text></Col>
            <Col span={24}>
              <Tag color={result.confidence === 'EXACT' ? 'green' : 'orange'}>
                Confidence: {result.confidence}
              </Tag>
            </Col>
          </Row>
        </Card>
      )}
    </Modal>
  )
}

// ─── Waste Categories tab ─────────────────────────────────────────
// Sprint carding-waste-categories — lets admins add/rename/reorder/
// deactivate the carding waste breakdown (Flats, Licker-in, Sweeping,
// Droppings, Seeds, Trash, Other by default) without a code change.
// There is no delete endpoint by design — existing shift closures
// reference categories by id, so they're deactivated, never deleted.

function slugifyCode(name: string): string {
  return name
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

function WasteCategoriesTab() {
  const qc = useQueryClient()
  const canCreate = usePermission('production.create')
  const canUpdate = usePermission('production.update')
  const [addOpen, setAddOpen] = useState(false)
  const [editingCat, setEditingCat] = useState<CardingWasteCategory | null>(null)

  const listQ = useQuery({
    queryKey: ['carding-waste-categories', 'all'],
    queryFn: () => checkpointsApi.getCardingWasteCategories({ active_only: false }),
  })

  // Prefix-matches the Daily Shift Log banner's exact ['carding-waste-categories']
  // key too, so one invalidation refreshes both this table and that banner.
  const invalidateAll = () => qc.invalidateQueries({ queryKey: ['carding-waste-categories'] })

  const toggleActiveMut = useMutation({
    mutationFn: (args: { id: string; is_active: boolean }) =>
      checkpointsApi.updateCardingWasteCategory(args.id, { is_active: args.is_active }),
    onSuccess: () => {
      message.success('Category updated')
      invalidateAll()
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Failed to update category'),
  })

  return (
    <>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col span={16}>
          <Title level={4} style={{ marginBottom: 0 }}>Carding Waste Categories</Title>
          <Text type="secondary">
            Drives the per-category breakdown on the Daily Shift Log's Carding
            section-waste closure (Flats, Licker-in, Sweeping, Droppings, Seeds,
            Trash, Other by default). Add, rename, reorder or deactivate here —
            there's no delete, since past shift closures reference these by id.
          </Text>
        </Col>
        {canCreate && (
          <Col>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>
              Add category
            </Button>
          </Col>
        )}
      </Row>

      <Table<CardingWasteCategory>
        size="small"
        rowKey="id"
        loading={listQ.isLoading}
        dataSource={listQ.data ?? []}
        pagination={false}
        columns={[
          {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (v: string, r) => (
              <Space size={6}>
                <Text style={r.is_active ? undefined : { color: 'rgba(0,0,0,0.45)' }}>{v}</Text>
                {!r.is_active && <Tag>Inactive</Tag>}
              </Space>
            ),
          },
          {
            title: 'Code',
            dataIndex: 'code',
            key: 'code',
            render: (v: string, r) => (
              <Text
                type="secondary"
                style={{ fontFamily: 'monospace', opacity: r.is_active ? 1 : 0.6 }}
              >
                {v}
              </Text>
            ),
          },
          {
            title: 'Sort order',
            dataIndex: 'sort_order',
            key: 'sort_order',
            align: 'right' as const,
            width: 110,
          },
          {
            title: 'Active',
            dataIndex: 'is_active',
            key: 'is_active',
            width: 90,
            render: (active: boolean, r) => (
              <Switch
                checked={active}
                disabled={!canUpdate}
                loading={toggleActiveMut.isPending && toggleActiveMut.variables?.id === r.id}
                onChange={(checked) => toggleActiveMut.mutate({ id: r.id, is_active: checked })}
              />
            ),
          },
          {
            title: '',
            key: 'action',
            width: 90,
            render: (_, r) => canUpdate ? (
              <Button size="small" icon={<EditOutlined />} onClick={() => setEditingCat(r)}>
                Edit
              </Button>
            ) : null,
          },
        ]}
      />

      <AddCategoryModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onDone={invalidateAll}
      />
      <EditCategoryModal
        category={editingCat}
        onClose={() => setEditingCat(null)}
        onDone={invalidateAll}
      />
    </>
  )
}

function AddCategoryModal({ open, onClose, onDone }: {
  open: boolean
  onClose: () => void
  onDone: () => void
}) {
  const [form] = Form.useForm()
  const [codeTouched, setCodeTouched] = useState(false)

  const reset = () => {
    onClose()
    form.resetFields()
    setCodeTouched(false)
  }

  const createMut = useMutation({
    mutationFn: (body: { code: string; name: string; sort_order?: number }) =>
      checkpointsApi.createCardingWasteCategory(body),
    onSuccess: () => {
      message.success('Category created')
      reset()
      onDone()
    },
    onError: (e: any) => {
      if (e?.response?.status === 409) {
        message.error('A category with that code already exists.')
      } else {
        message.error(e?.response?.data?.detail ?? 'Failed to create category')
      }
    },
  })

  return (
    <Modal
      title="Add Waste Category"
      open={open}
      onCancel={reset}
      footer={null}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ sort_order: 100 }}
        onValuesChange={(changed) => {
          if ('name' in changed && !codeTouched) {
            form.setFieldsValue({ code: slugifyCode(changed.name ?? '') })
          }
        }}
        onFinish={(v) => createMut.mutate({
          code: v.code,
          name: v.name,
          sort_order: v.sort_order,
        })}
      >
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: 'Name is required' }]}
        >
          <Input placeholder="e.g. Licker-in" />
        </Form.Item>
        <Form.Item
          name="code"
          label="Code"
          rules={[{ required: true, message: 'Code is required' }]}
          tooltip="Auto-suggested from the name — the stable identifier used internally. Editable."
        >
          <Input placeholder="e.g. LICKER_IN" onChange={() => setCodeTouched(true)} />
        </Form.Item>
        <Form.Item name="sort_order" label="Sort order">
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={createMut.isPending}>
              Create
            </Button>
            <Button onClick={reset}>Cancel</Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  )
}

function EditCategoryModal({ category, onClose, onDone }: {
  category: CardingWasteCategory | null
  onClose: () => void
  onDone: () => void
}) {
  const [form] = Form.useForm()

  const updateMut = useMutation({
    mutationFn: (args: { id: string; body: { name?: string; sort_order?: number } }) =>
      checkpointsApi.updateCardingWasteCategory(args.id, args.body),
    onSuccess: () => {
      message.success('Category updated')
      onClose()
      onDone()
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Failed to update category'),
  })

  return (
    <Modal
      title={category ? `Edit ${category.name}` : 'Edit Category'}
      open={!!category}
      onCancel={onClose}
      footer={null}
      destroyOnClose
    >
      {category && (
        <Form
          form={form}
          layout="vertical"
          initialValues={{ name: category.name, sort_order: category.sort_order }}
          onFinish={(v) => updateMut.mutate({
            id: category.id,
            body: { name: v.name, sort_order: v.sort_order },
          })}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Name is required' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item label="Code" tooltip="Not editable after creation — it's the stable identifier.">
            <Input value={category.code} disabled />
          </Form.Item>
          <Form.Item name="sort_order" label="Sort order">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={updateMut.isPending}>
                Save
              </Button>
              <Button onClick={onClose}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      )}
    </Modal>
  )
}
